import React from 'react';

import { Platform, SubscriptionTier } from '@invoice-simple/common';
import {
  getMaxCouponDiscount,
  PaywallCouponConfig,
  PaywallPriceData
} from '@invoice-simple/is-coupon';

import { ISIntl } from 'src/i18n';
import UserModel, { Cadence } from 'src/models/UserModel';
import { getAnnualDiscount, hasValidDiscount } from '../utils';
import { messages } from '../utils/messages';
import { PlanIntervalButton } from './PlanIntervalButton';

interface PlanIntervalSelectorV2Props {
  selectedInterval: Cadence;
  onToggleInterval: (interval: Cadence) => void;
  formatMessage: ISIntl['formatMessage'];
  isHidden?: boolean;
  couponInfo?: PaywallCouponConfig;
  className?: string;
  user: UserModel;
}

export const PlanIntervalSelectorV2 = ({
  selectedInterval,
  onToggleInterval,
  formatMessage,
  isHidden,
  couponInfo,
  className,
  user
}: PlanIntervalSelectorV2Props) => {
  if (isHidden) {
    return null;
  }

  const annaulBadgeText = () => {
    const hasDiscount = couponInfo && hasValidDiscount(couponInfo, Cadence.ANNUAL);

    const priceData = [
      SubscriptionTier.ESSENTIALS,
      SubscriptionTier.PLUS,
      SubscriptionTier.PREMIUM
    ].flatMap((tier) => {
      return [Cadence.MONTHLY, Cadence.ANNUAL].map((cadence) => {
        return {
          packageId: `${tier}_${cadence}`,
          price: user.getGeoSubAmount({ cadence, tier }),
          sku: user.getGeoSubOrderSku({ cadence, tier })
        };
      });
    }) as PaywallPriceData;

    if (hasDiscount) {
      const discount = getMaxCouponDiscount({
        paywallCouponConfig: couponInfo,
        platform: Platform.WEB,
        cadence: Cadence.ANNUAL,
        countryCode: user.activeCountryCode,
        priceData
      });
      return formatMessage(messages.badgeUpToDiscount, { discount });
    }

    const discountPercentage = getAnnualDiscount(SubscriptionTier.PREMIUM);

    return formatMessage(messages.discountedPaywallBadge, { discount: discountPercentage });
  };

  return (
    <div className={`relative flex justify-center ${className}`}>
      <div data-testid="interval-select" className="flex w-fit rounded-lg bg-slate-100">
        <PlanIntervalButton
          className="rounded-l-lg"
          interval={Cadence.MONTHLY}
          isSelected={Cadence.MONTHLY === selectedInterval}
          onClick={() => onToggleInterval(Cadence.MONTHLY)}
          buttonText={formatMessage(messages.monthly)}
        />
        <PlanIntervalButton
          className="rounded-r-lg"
          interval={Cadence.ANNUAL}
          isSelected={Cadence.ANNUAL === selectedInterval}
          onClick={() => onToggleInterval(Cadence.ANNUAL)}
          buttonText={formatMessage(messages.annual)}
          badgeText={annaulBadgeText()}
        />
      </div>
    </div>
  );
};
